/**
 * Registry of fields that hold money.
 *
 * The source of truth is the **Sterling Fields inventory**, which marks every UI
 * field Yes (Sterling format required) or No. This file encodes that inventory.
 *
 * Deliberately an explicit allowlist rather than a name heuristic: a pattern like
 * /value|amount|price|total/ would falsely capture `total_expense_ratio` and
 * `loan_valuation_percentage` (percentages), `value_date` / `price_date` /
 * `amount_outstanding_date` (dates), `share_current_number` and `fund_units`
 * (quantities), `amount_flag`, `payment_count`, and the `*_type` / `*_frequency`
 * code fields — all of which the inventory marks No.
 *
 * Form-driven screens do not need to consult this list — those fields carry
 * `"format": "currency"` in the served form metadata
 * (`cloud-assyst-services/src/middleware/form/`). This registry covers grid
 * columns, calculation tables and hand-built summary panels, which have no such
 * metadata.
 */

/** Fields the inventory marks "Yes" wherever they appear. */
export const MONETARY_FIELDS: ReadonlySet<string> = new Set([
  // ── Amounts ──────────────────────────────────────────────────────────────
  "amount",
  "amount_borrowed",
  "amount_outstanding",
  "amount_to_company",
  "client_amount",
  "partner_amount",
  "joint_amount",
  "gross_amount",
  "net_amount",
  "payment_amount",
  "paid_amount",

  // ── Valuations and balances ──────────────────────────────────────────────
  "current_value",
  "balance_value",
  "fund_value",
  "total_fund_value",
  "future_value",
  "property_value",
  "home_original_value",
  "share_original_value",
  "surrender_value",
  "transfer_value",
  "valuation_amount",

  // ── Benefits and premiums ────────────────────────────────────────────────
  "premium",
  "sum_assured",
  "lump_sum_benefit",
  "lump_sum_benefits",
  "periodic_benefit",

  // ── Commission receipts / distribution ───────────────────────────────────

  // ── Income / outgoings calculation summaries ─────────────────────────────
  "ClientMonthlyGrossAmount",
  "PartnerMonthlyGrossAmount",
  "JointMonthlyGrossAmount",
  "ClientAnnualGrossAmount",
  "PartnerAnnualGrossAmount",
  "JointAnnualGrossAmount",
  "ClientMonthlyNetAmount",
  "PartnerMonthlyNetAmount",
  "JointMonthlyNetAmount",
  "ClientAnnualNetAmount",
  "PartnerAnnualNetAmount",
  "JointAnnualNetAmount",
  "MonthlyClientAmount",
  "MonthlyPartnerAmount",
  "MonthlyJointAmount",
  "AnnualClientAmount",
  "AnnualPartnerAmount",
  "AnnualJointAmount",

  // ── Payment / withdrawal calculation tables ──────────────────────────────
  "currentPremium",
  "totalSinglePremiums",
  "totalPaidToDate",
  "currentRegular",
  "currentRegularWithdrawal",
  "total_fund",
]);

/**
 * Fields that are monetary only within a specific table or screen, keyed by
 * table/screen id. Used for keys too generic to allowlist globally — `value` is a
 * monetary column on Case Summary but a free-form attribute value elsewhere.
 */
export const MONETARY_FIELDS_BY_TABLE: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  CASE_SUMMARY: new Set(["value"]),

  // Commission keys are short, generic words that mean money *here* and could
  // mean anything on another screen. `expected` / `actual` in particular are
  // only amounts inside the receipting and distribution grids, so they are
  // scoped rather than registered globally.
  COMMISSION_RECEIPTS: new Set([
    "expected",
    "actual",
    "benefit",
    "receiptedAmount",
    "targetedAmount",
    "amountReceived",
    "unusedAmount",
    "totalAmount",
    "ExpectedAmount",
    "ActualAmount",
    "GrossAmount",
  ]),
  COMMISSION_DISTRIBUTION: new Set([
    "expected",
    "actual",
    "standardAmount",
    "ruleAmount",
    "receivedAmount",
    "distributedAmount",
    "totalAmount",
  ]),

  // Policy benefit columns (Case Selection, policy grids). `benefit` reads as
  // free text in some models, so it is never monetary by default.
  POLICY: new Set(["benefit"]),
};

/**
 * Monetary fields the inventory marks **No**, held at higher precision than the
 * standard two decimals.
 *
 * The requirement was clarified from the client-report perspective: a client
 * comparing fund units × fund price against the fund value must not see figures
 * that appear to contradict each other. `unit_price` defaults to `0.00000000`
 * (8 dp) and `share_current_price` to `0.0000` (4 dp), and the form derives
 * `unit_price = fund_value / fund_units` at 4 dp — so forcing two decimals could
 * display `0.0007` as "£0.00" or `1.23456789` as "£1.23" and make the arithmetic
 * look wrong.
 *
 * These keep their native precision and are never Sterling-formatted.
 */
export const HIGH_PRECISION_MONETARY_FIELDS: ReadonlySet<string> = new Set([
  "unit_price",
  "share_current_price",
]);

/**
 * Fields that look monetary but are not — every one marked "No" in the
 * inventory. Kept explicit so the exclusions are documented and testable rather
 * than implied by absence.
 */
export const NON_MONETARY_LOOKALIKES: ReadonlySet<string> = new Set([
  // Percentages and rates
  "total_expense_ratio",
  "loan_valuation_percentage",
  "bank_interest_rate",
  "interest_rate",
  "interest_basis",
  // Quantities, counts and codes
  "fund_units",
  "fund_code",
  "share_current_number",
  "payment_count",
  "number_of_payment",
  "term",
  // Dates
  "value_date",
  "balance_date",
  "price_date",
  "salary_review_date",
  "amount_outstanding_date",
  "acquired_date",
  "start_date",
  "end_date",
  // Flags, types and frequencies
  "amount_flag",
  "income_type",
  "commission_type",
  "payment_type",
  "payment_source",
  "source_of_payment",
  "benefit_frequency",
  "benefits_indexed",
  "benefit_text",
  "premium_indexed",
  "repayment_method",
  "frequency",
  "currency_code",
]);

/** True when `field` should be rendered as UK Sterling. */
export const isMonetaryField = (field: string, table?: string): boolean => {
  if (!field) return false;
  if (NON_MONETARY_LOOKALIKES.has(field)) return false;
  if (HIGH_PRECISION_MONETARY_FIELDS.has(field)) return false;
  if (MONETARY_FIELDS.has(field)) return true;
  if (table && MONETARY_FIELDS_BY_TABLE[table]?.has(field)) return true;
  return false;
};

/** True when served form metadata marks the field as currency. */
export const isCurrencyFormatted = (
  element: { format?: string } | null | undefined,
): boolean => element?.format === "currency";
